import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import AnamnesisCatalogFormModal from '../anamnesisCatalogFormModal';
import { useAnamnesisCatalog } from '../hooks/useAnamnesisCatalog';
import { useDeleteAnamnesisCatalog } from '../hooks/useDeleteAnamnesisCatalog';

function AnamnesisCatalogListWeb() {
  const navigate = useNavigate();

  const { questions, isLoading, error: listError, refetch: getQuestions } = useAnamnesisCatalog();
  const { remove } = useDeleteAnamnesisCatalog();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, questionId) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuestionId(questionId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedQuestion(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const q = questions.find((item) => item.id === selectedQuestionId);
    if (q) {
      setSelectedQuestion(q);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedQuestion(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedQuestionId(null);
  };

  const handleQuestionSaved = () => {
    getQuestions();
    setSuccessMessage('Pergunta salva com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedQuestionId)
      .then((res) => {
        getQuestions();
        setSuccessMessage(res.data ?? 'Pergunta excluída com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir pergunta.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedQuestionId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                Perguntas da Anamnese
              </Typography>
              <Button variant="outlined" onClick={openCreateModal}>
                Adicionar Pergunta
              </Button>
            </Box>

            <List>
              {questions.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhuma pergunta cadastrada
                </Typography>
              ) : (
                questions.map((q) => (
                  <React.Fragment key={q.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, q.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={q.description}
                        secondary={q.order != null ? `#${q.order}` : undefined}
                        secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={openEditModal}>Editar</MenuItem>
          <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
            Excluir
          </MenuItem>
        </Menu>

        <AnamnesisCatalogFormModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={handleQuestionSaved}
          question={selectedQuestion}
        />

        <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você tem certeza que deseja excluir esta pergunta? Essa ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default AnamnesisCatalogListWeb;
